import React, { useEffect, useState } from "react";
import SeopageSearch from "../containers/Index/Search/SeopageSearch";
import parkRideImage from "../common/images/G-1.webp";
import parkRideImage2 from "../common/images/G-2.webp";

import axios from "axios";
import apiUrl from "../common/config/apiUrl";
import apiKey from "../common/config/apiKey";
import Seo from "../common/Components/Seo/Seo";
import ParkAndRideSchema from "../common/Components/StructuredData/ParkAndRideSchema";

// React Icons
import {
  FaCar,
  FaBus,
  FaShieldAlt,
  FaSave,
  FaParking,
  FaClock,
  FaCheckCircle,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaUsers,
  FaLightbulb,
  FaMapMarkerAlt,
} from "react-icons/fa";

const ParkAndRideParking = () => {
  const [airports, setAirports] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(`${apiUrl}/airports?key=${apiKey}`);
        if (response?.data?.response_code == 200) {
          setAirports(response?.data?.data);
        }
      } catch (error) {
        console.log("error", error);
      }
    })();
  }, []);

  // SEO data for Park & Ride
  const parkRideSEOData = {
    title:
      "Park & Ride Airport Parking | How It Works & Money-Saving Tips | Airport Parking Deals",
    description:
      "Your guide to affordable Park & Ride airport parking. Discover how it works, why it's the budget-friendly choice, and how to book the best secure off-airport parking.",
    keywords:
      "park and ride airport parking, off airport parking, budget airport parking, airport shuttle parking, secure airport parking, airport parking deals, cheap airport parking",
    url: `${
      process.env.REACT_APP_SITE_URL || "https://airportparkingdeals.co.uk"
    }/park-and-ride-parking`,
  };


  return (
    <div>
      <Seo
        title={parkRideSEOData.title}
        description={parkRideSEOData.description}
        keywords={parkRideSEOData.keywords}
        url={parkRideSEOData.url}
        type="website"
      />
      <ParkAndRideSchema />
      <SeopageSearch
        airports={airports}
        airportName=""
        heroTitle="<span class='text-secondary'>Park & Ride:</span> Smart, Secure & Great Value"
        description1="For a reliable and budget-friendly start to your journey, Park & Ride is the popular choice."
        description2="It's the perfect blend of security, convenience, and value, giving you peace of mind without the premium price tag of terminal parking."
        features={[
          {
            title: "Budget-Friendly",
            description:
              "The most cost-effective option for trips of any length",
            icon: <FaMoneyBillWave />,
          },
          {
            title: "Secure Compounds",
            description: "CCTV, fencing, and Park Mark® security awards",
            icon: <FaShieldAlt />,
          },
          {
            title: "Free Shuttle Service",
            description: "Frequent transfers running every 10-15 minutes",
            icon: <FaBus />,
          },
          {
            title: "Off-Airport Parking",
            description: "Secure facilities just minutes from the terminal",
            icon: <FaParking />,
          },
          {
            title: "Great for Families",
            description: "Perfect for budget-conscious travelers",
            icon: <FaUsers />,
          },
        ]}
      />

      {/* Hero Section */}
      <section className="md:py-10 py-7 bg-gray-50">
        <div className="px-5 mx-auto max-w-[1280px] sm:px-6 lg:px-8">
          <div className="grid items-center gap-5 lg:grid-cols-2">
            <div>
              <div className="flex flex-wrap items-center justify-center gap-2 mb-2">
                <h1 className="text-3xl font-bold md:text-5xl">
                  Park & Ride Airport Parking:
                  <span className="px-1 font-bold text-teal-500">
                    Smart, Secure & Great Value
                  </span>
                </h1>
              </div>

              <div className="space-y-4 text-gray-700">
                <p>
                  For a reliable and budget-friendly start to your journey,{" "}
                  <strong>Park & Ride</strong> is the popular choice. It's the
                  perfect blend of security, convenience, and value, giving you
                  peace of mind without the premium price tag of terminal
                  parking.
                </p>
                <p>
                  Park your car in a secure, off-airport facility and catch a
                  complimentary shuttle directly to your terminal.{" "}
                  <strong>
                    Simple, affordable, and stress-free airport parking.
                  </strong>
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="relative">
                <div className="h-full">
                  <img
                    src={parkRideImage}
                    alt="Park & Ride Airport Parking - Affordable and Secure"
                    className="object-cover w-full h-full rounded-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 bg-white md:py-16">
        <div className="px-4 mx-auto max-w-[1280px] sm:px-6 md:px-10">
          <div className="mb-12 text-center md:mb-16">
            <h2 className="mb-4 text-2xl font-bold leading-tight text-center sm:text-3xl md:text-4xl text-primary">
              <span className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                <FaBus className="text-secondary" />
                <span>
                  How Park & Ride Works:{" "}
                  <span className="text-teal-500">Your Easy Journey</span>
                </span>
              </span>
            </h2>
            <p className="max-w-2xl mx-auto text-sm leading-relaxed text-gray-600 sm:text-base">
              Three simple steps to hassle-free airport parking
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {/* Step 1 */}
            <div className="relative p-6 transition-all duration-300 transform shadow-lg bg-gradient-to-br from-blue-50 to-teal-50 group sm:p-8 rounded-2xl hover:shadow-xl hover:-translate-y-1">
              <div className="absolute top-4 right-4">
                <span className="flex items-center justify-center w-12 h-12 text-xl font-bold text-white rounded-full bg-primary">
                  1
                </span>
              </div>
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-12 h-12 mr-4 transition-colors duration-300 rounded-full bg-primary group-hover:bg-secondary">
                  <FaParking className="text-xl text-white" />
                </div>
                <h3 className="text-xl font-bold transition-colors duration-300 sm:text-2xl text-primary group-hover:text-secondary">
                  Park & Secure
                </h3>
              </div>
              <p className="leading-relaxed text-gray-700">
                Drive to a secure, off-airport car park, often just a few
                minutes from the terminal. Check in at the barrier using your
                booking reference, and park your car in a designated bay.
              </p>
            </div>

            {/* Step 2 */}
            <div className="relative p-6 transition-all duration-300 transform shadow-lg bg-gradient-to-br from-blue-50 to-teal-50 group sm:p-8 rounded-2xl hover:shadow-xl hover:-translate-y-1">
              <div className="absolute top-4 right-4">
                <span className="flex items-center justify-center w-12 h-12 text-xl font-bold text-white rounded-full bg-primary">
                  2
                </span>
              </div>
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-12 h-12 mr-4 transition-colors duration-300 rounded-full bg-primary group-hover:bg-secondary">
                  <FaBus className="text-xl text-white" />
                </div>
                <h3 className="text-xl font-bold transition-colors duration-300 sm:text-2xl text-primary group-hover:text-secondary">
                  Catch the Shuttle
                </h3>
              </div>
              <p className="leading-relaxed text-gray-700">
                Head to the clearly marked bus stop. A frequent, complimentary
                shuttle bus will whisk you and your luggage directly to your
                terminal. Shuttles typically run every 10-15 minutes.
              </p>
            </div>

            {/* Step 3 */}
            <div className="relative p-6 transition-all duration-300 transform shadow-lg bg-gradient-to-br from-blue-50 to-teal-50 group sm:p-8 rounded-2xl hover:shadow-xl hover:-translate-y-1">
              <div className="absolute top-4 right-4">
                <span className="flex items-center justify-center w-12 h-12 text-xl font-bold text-white rounded-full bg-primary">
                  3
                </span>
              </div>
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-12 h-12 mr-4 transition-colors duration-300 rounded-full bg-primary group-hover:bg-secondary">
                  <FaCar className="text-xl text-white" />
                </div>
                <h3 className="text-xl font-bold transition-colors duration-300 sm:text-2xl text-primary group-hover:text-secondary">
                  Return & Collect
                </h3>
              </div>
              <p className="leading-relaxed text-gray-700">
                On your return, catch the shuttle bus back from the designated
                airport stop to the car park. Hop in your car and drive straight
                home—it's that simple.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-secondary/10 to-white">
        <div className="px-4 mx-auto max-w-[1280px] sm:px-6 md:px-10">
          <div className="mb-12 text-center">
            <h2 className="flex items-center justify-center gap-3 mb-4 text-2xl font-bold sm:text-3xl md:text-4xl text-primary">
              <FaCheckCircle className="hidden text-secondary md:block" />
              Why Choose Park & Ride?
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* Great Value */}
            <div className="p-4 transition-all duration-300 bg-white border-2 border-teal-100 shadow-lg rounded-2xl hover:shadow-xl">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/10">
                <FaMoneyBillWave className="text-3xl text-secondary" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-center text-primary">
                Great Value
              </h3>
              <p className="leading-relaxed text-center text-gray-700">
                The most cost-effective option for trips of any length,
                especially for families and those travelling on a budget.
              </p>
            </div>

            {/* Secure Compounds */}
            <div className="p-4 transition-all duration-300 bg-white border-2 border-teal-100 shadow-lg rounded-2xl hover:shadow-xl">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/10">
                <FaShieldAlt className="text-3xl text-secondary" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-center text-primary">
                Secure Compounds
              </h3>
              <p className="leading-relaxed text-center text-gray-700">
                Our partnered car parks feature CCTV, perimeter fencing, and
                regular patrols, with many holding Park Mark® security awards.
              </p>
            </div>

            {/* Hassle-Free */}
            <div className="p-4 transition-all duration-300 bg-white border-2 border-teal-100 shadow-lg rounded-2xl hover:shadow-xl">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/10">
                <FaCheckCircle className="text-3xl text-secondary" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-center text-primary">
                Hassle-Free
              </h3>
              <p className="leading-relaxed text-center text-gray-700">
                No stressful airport driving or expensive terminal drop-off
                charges. Just park and relax.
              </p>
            </div>

            {/* Convenient for All */}
            <div className="p-4 transition-all duration-300 bg-white border-2 border-teal-100 shadow-lg rounded-2xl hover:shadow-xl">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/10">
                <FaUsers className="text-3xl text-secondary" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-center text-primary">
                Convenient for All
              </h3>
              <p className="leading-relaxed text-center text-gray-700">
                Modern shuttle buses are equipped with luggage space and are
                accessible for all passengers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Top Tips Section */}
      <section className="py-12 bg-white md:py-16">
        <div className="px-4 mx-auto max-w-[1280px] sm:px-6 md:px-10">
          <div className="mb-12 text-center">
            <h2 className="flex items-center justify-center gap-3 mb-4 text-2xl font-bold sm:text-3xl md:text-4xl text-primary">
              <FaLightbulb className="hidden text-secondary md:block " />
              Top Tips for a{" "}
              Smooth Experience
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {/* Tip 1 */}
            <div className="p-6 shadow-lg bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl">
              <div className="flex items-center mb-4">
                <FaClock className="mr-3 text-3xl text-primary" />
                <h3 className="text-xl font-bold text-primary">
                  Allow Extra Time
                </h3>
              </div>
              <p className="leading-relaxed text-gray-700">
                Factor in the shuttle transfer when planning your journey. We
                recommend arriving at the car park 30 minutes earlier than you
                would for a terminal drop-off.
              </p>
            </div>

            {/* Tip 2 */}
            <div className="p-6 shadow-lg bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl">
              <div className="flex items-center mb-4">
                <FaCheckCircle className="mr-3 text-3xl text-primary" />
                <h3 className="text-xl font-bold text-primary">
                  Keep Essentials Handy
                </h3>
              </div>
              <p className="leading-relaxed text-gray-700">
                Have your booking confirmation ready for the entry barrier and
                keep your ticket for the return journey.
              </p>
            </div>

            {/* Tip 3 */}
            <div className="p-6 shadow-lg bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl">
              <div className="flex items-center mb-4">
                <FaMapMarkerAlt className="mr-3 text-3xl text-primary" />
                <h3 className="text-xl font-bold text-primary">
                  Note Your Spot
                </h3>
              </div>
              <p className="leading-relaxed text-gray-700">
                It sounds simple, but make a note of your parking row and zone.
                After a long flight, you'll thank yourself!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* When to Book Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-secondary/10 to-white">
        <div className="px-4 mx-auto max-w-[1280px] sm:px-6 md:px-10">
          <div className="mb-8 text-center">
            <h2 className="flex items-center justify-center gap-3 mb-4 text-2xl font-bold sm:text-3xl md:text-4xl text-primary">
              <FaCalendarAlt className="text-secondary" />
              When to Book Your Park & Ride Space
            </h2>
          </div>

          <div className="max-w-4xl p-8 mx-auto bg-white shadow-xl rounded-2xl">
            <p className="mb-6 text-lg leading-relaxed text-gray-700">
              For the best prices and guaranteed availability,{" "}
              <strong className="text-primary">
                book as early as you book your flights
              </strong>
              . Early booking is especially crucial during school holidays and
              peak summer months when the most affordable car parks sell out
              first.
            </p>
            <div className="flex items-start p-4 rounded-lg bg-teal-50">
              <FaLightbulb className="flex-shrink-0 mt-1 mr-3 text-2xl text-secondary" />
              <p className="leading-relaxed text-gray-700">
                <strong>Pro Tip:</strong> Booking months in advance not only
                secures your space but often saves you up to 60% compared to
                last-minute bookings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-primary to-secondary">
        <div className="px-4 mx-auto max-w-[1280px] text-center">
          <h2 className="mb-6 text-3xl font-bold text-white md:text-4xl">
            Ready for a Stress-Free, Great Value Airport Parking Solution?
          </h2>
          <p className="max-w-3xl mx-auto mb-8 text-lg leading-relaxed text-white/90">
            Let Airport Parking Deals compare the best-rated, secure Park & Ride
            options for you. Find the perfect balance of price and convenience
            for your next trip.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/#search-form"
              className="px-8 py-4 text-lg font-semibold transition-all duration-300 transform bg-white rounded-full text-primary hover:bg-gray-100 hover:shadow-xl hover:-translate-y-1"
            >
              Compare Park & Ride Prices
            </a>
            <a
              href="/contact-us"
              className="px-8 py-4 text-lg font-semibold text-white transition-all duration-300 transform border-2 border-white rounded-full hover:bg-white hover:text-primary hover:-translate-y-1"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Benefits Comparison Section */}
      <section className="py-12 bg-white md:py-16">
        <div className="px-4 mx-auto max-w-[1280px] sm:px-6 md:px-10">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div className="flex justify-center order-2 lg:order-1">
              <img
                src={parkRideImage2}
                alt="Secure Park & Ride Facilities"
                className="object-cover w-full h-full"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="mb-6 text-3xl font-bold md:text-4xl text-primary">
                Perfect for{" "}
                <span className="text-teal-500">Budget-Conscious Travelers</span>
              </h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <FaCheckCircle className="flex-shrink-0 mt-1 mr-3 text-2xl text-secondary" />
                  <div>
                    <h3 className="mb-2 text-xl font-semibold text-primary">
                      Save Money Without Compromise
                    </h3>
                    <p className="text-gray-700">
                      Get the same level of security and service as premium
                      options, but at a fraction of the cost.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaCheckCircle className="flex-shrink-0 mt-1 mr-3 text-2xl text-secondary" />
                  <div>
                    <h3 className="mb-2 text-xl font-semibold text-primary">
                      Ideal for Long Trips
                    </h3>
                    <p className="text-gray-700">
                      The longer your trip, the more you save. Park & Ride
                      offers unbeatable value for extended holidays.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaCheckCircle className="flex-shrink-0 mt-1 mr-3 text-2xl text-secondary" />
                  <div>
                    <h3 className="mb-2 text-xl font-semibold text-primary">
                      Family-Friendly
                    </h3>
                    <p className="text-gray-700">
                      Spacious shuttle buses accommodate families with luggage,
                      car seats, and strollers.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaCheckCircle className="flex-shrink-0 mt-1 mr-3 text-2xl text-secondary" />
                  <div>
                    <h3 className="mb-2 text-xl font-semibold text-primary">
                      Close to Airport
                    </h3>
                    <p className="text-gray-700">
                      Most Park & Ride facilities are just 5-10 minutes from
                      the terminal, making transfers quick and easy.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ParkAndRideParking;

